import { RawAlgoliaProduct } from '@/types/Search';
import {
  PRODUCT_CARD_IMG_HEIGHT,
  PRODUCT_CARD_IMG_WIDTH,
  PRODUCT_DETAIL_IMG_HEIGHT,
  PRODUCT_DETAIL_IMG_WIDTH
} from '@/const/product';
import qs from 'qs';

export const DEFAULT_PRODUCT_CARD_SETTINGS: CDNImageOptions = {
  height: PRODUCT_CARD_IMG_HEIGHT,
  width: PRODUCT_CARD_IMG_WIDTH,
  format: 'webp'
};

export const DEFAULT_PRODUCT_DETAIL_SETTINGS: CDNImageOptions = {
  height: PRODUCT_DETAIL_IMG_HEIGHT,
  width: PRODUCT_DETAIL_IMG_WIDTH,
  format: 'webp'
};

export type CDNImageOptions = {
  height?: number;
  width?: number;
  format?: 'webp' | 'jpg' | 'png';
};

export function getCDNUrlForImage(
  imageName: string,
  options: CDNImageOptions = DEFAULT_PRODUCT_CARD_SETTINGS
): string {
  if (!imageName || imageName === '' || imageName === 'null') {
    return '';
  }
  const CDN_URL = process.env.NEXT_PUBLIC_CLOUDFRONT_CDN_URL as string;
  const query = qs.stringify(options, { indices: false });
  return `${CDN_URL}/${imageName}?${query}`;
}

/**
 * Returns images to be used for product cards
 * directly from the algolia result
 * @param algoliaProduct - the raw response from Algolia regarding the product itself
 * @param imageOptions - Optional image options to send to the CDN for sizing purposes
 * */
export function algoliaResultToProductCardImages(
  algoliaProduct: RawAlgoliaProduct,
  imageOptions: CDNImageOptions = DEFAULT_PRODUCT_CARD_SETTINGS
): { mainImage: string; hoverImage: string } {
  const { ep_images } = algoliaProduct;
  if (!ep_images) {
    console.warn(
      `[WARNING] - ep_images is undefined for product id ${algoliaProduct.objectID}| SKU: ${algoliaProduct.ep_sku}`
    );
    return {
      mainImage: '',
      hoverImage: ''
    };
  }

  const mainImage = getCDNUrlForImage(ep_images.main, imageOptions);
  const hoverImage = getCDNUrlForImage(ep_images.hover, imageOptions);

  return {
    mainImage,
    hoverImage
  };
}

/**
 * Centralized helper to route external Revolve/FWRD image URLs through our HTTPS proxy
 * to avoid mixed content and invalid upstream TLS certs.
 */
export function getProxiedImageSrc(src: string): string {
  if (!src) return src;
  try {
    const url = new URL(src);
    const host = url.hostname;
    if (
      host.endsWith('revolveclothing.com') ||
      host.endsWith('revolveassets.com') ||
      host.endsWith('fwrdassets.com')
    ) {
      // Prefer http upstream to avoid bad https certs; our proxy serves over https
      url.protocol = 'http:';
      return `/api/image-proxy?src=${encodeURIComponent(url.toString())}`;
    }
    return src;
  } catch {
    return src;
  }
}
